import { Link, useParams } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { Badge, Button, Card, CardContent } from '@/components/ui'
import { getComparisonBySlug } from '@/data/comparisons'
import { useSeo } from '@/lib/seo'

export function BrandComparisonPage() {
  const { comparison } = useParams<{ comparison: string }>()
  const data = comparison ? getComparisonBySlug(comparison) : undefined

  if (data) {
    useSeo({
      title: data.seo.title,
      description: data.seo.description,
      image: `/brand-images/${data.brand1.slug}.jpg`,
    })
  }

  if (!data) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="container-page py-16">
          <Card className="max-w-xl mx-auto text-center">
            <CardContent className="py-10">
              <h1 className="text-2xl font-bold text-gray-900">Comparison not found</h1>
              <p className="mt-2 text-gray-600">
                We couldn’t find that comparison. Try browsing all comparisons.
              </p>
              <Link to="/brands/compare" className="inline-block mt-6">
                <Button>Browse comparisons</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-page py-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/brands" className="hover:underline">
              Brands
            </Link>
            <span>/</span>
            <Link to="/brands/compare" className="hover:underline">
              Compare
            </Link>
            <span>/</span>
            <span className="font-medium text-gray-900">
              {data.brand1.name} vs {data.brand2.name}
            </span>
          </div>

          <h1 className="mt-2 text-3xl font-bold text-gray-900">
            {data.brand1.name} vs {data.brand2.name}
          </h1>
          <p className="mt-2 text-gray-600 max-w-3xl">{data.seo.description}</p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link to={`/brands/${data.brand1.slug}`}>
              <Button size="lg">
                Explore {data.brand1.name} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to={`/brands/${data.brand2.slug}`}>
              <Button size="lg" variant="outline">
                Explore {data.brand2.name}
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Heritage</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-gray-900">{data.brand1.name}</p>
                    <p className="mt-2 text-sm text-gray-600">{data.heritage.brand1}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-gray-900">{data.brand2.name}</p>
                    <p className="mt-2 text-sm text-gray-600">{data.heritage.brand2}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Model matchups</h2>
                <p className="mt-1 text-sm text-gray-600">
                  These links point to the client-supplied brand/model pages we already wired in.
                </p>

                <div className="mt-4 space-y-3">
                  {data.modelMatchups.map((m) => (
                    <div
                      key={`${m.category}-${m.brand1Model}-${m.brand2Model}`}
                      className="rounded-lg border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-sm text-gray-600">{m.category}</p>
                        <p className="mt-1 font-semibold text-gray-900">
                          {m.brand1Model} <span className="text-gray-400">vs</span> {m.brand2Model}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Link to={m.brand1Link}>
                          <Button size="sm" variant="outline">
                            {data.brand1.name}
                          </Button>
                        </Link>
                        <Link to={m.brand2Link}>
                          <Button size="sm" variant="outline">
                            {data.brand2.name}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Verdict</h2>
                <p className="mt-3 text-gray-700">{data.verdict}</p>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Pricing snapshot</h2>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-gray-900">{data.brand1.name}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Avg: ${(data.pricing.brand1Avg / 100).toLocaleString()} · Inventory:{' '}
                      {data.pricing.brand1Count.toLocaleString()}
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <p className="font-semibold text-gray-900">{data.brand2.name}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      Avg: ${(data.pricing.brand2Avg / 100).toLocaleString()} · Inventory:{' '}
                      {data.pricing.brand2Count.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <h2 className="text-lg font-semibold text-gray-900">Reliability</h2>
                <div className="mt-4 space-y-3">
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-gray-900">{data.brand1.name}</p>
                      <Badge variant="default">{data.reliability.brand1Rating.toFixed(1)}/5</Badge>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{data.reliability.brand1Note}</p>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-gray-900">{data.brand2.name}</p>
                      <Badge variant="default">{data.reliability.brand2Rating.toFixed(1)}/5</Badge>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{data.reliability.brand2Note}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-primary-50 border-primary-200">
              <CardContent>
                <h3 className="font-semibold text-gray-900">Source</h3>
                <p className="mt-2 text-sm text-gray-600">
                  This comparison page is rendered from the client’s Next.js content copied under
                  `/br/`.
                </p>
                <Link to="/brands/compare" className="inline-block mt-4">
                  <Button>Back to comparisons</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}

